<template>
  <DashboardLayout>
    <template slot="navbarFilters">
      <div v-if="sellerOptions.length > 1 || marketplaceOptions.length > 1" class="navbar-filters fee-claims-filters">
        <select v-if="sellerOptions.length > 1" v-model="sellerId" class="fee-claims-filter" @change="load">
          <option value="">S.Accounts: All</option>
          <option v-for="s in sellerOptions" :key="s.id" :value="String(s.id)">{{ s.name }}</option>
        </select>
        <select v-if="marketplaceOptions.length > 1" v-model="marketplace" class="fee-claims-filter" @change="load">
          <option value="">Marketplaces: All</option>
          <option v-for="m in marketplaceOptions" :key="m" :value="m">Amazon.{{ mpSuffix(m) }}</option>
        </select>
      </div>
    </template>

    <div class="fee-claims">
      <div class="fee-claims-content">
        <h1 class="fee-claims-title">Fee Claims</h1>
        <p class="fee-claims-subtitle">When Amazon re-measures a product, you may be owed money on past orders, or paying too much on new ones.</p>

        <div v-if="notAvailable" class="fee-claims-empty">Fee Claims isn't switched on for this account.</div>
        <template v-else>
          <!-- KPIs (sums of the rows below, per currency: FeeClaimsController#kpis byCurrency) -->
          <div v-if="showKpis" class="fee-claims-kpis">
            <div class="fee-claims-kpi">
              <div class="k">Ready to claim</div>
              <div class="v green" :class="{ multi: kpiAmounts('readyToClaimCents').length > 1 }"><span v-for="(a, i) in kpiAmounts('readyToClaimCents')" :key="i">{{ a }}</span></div>
              <div class="s">{{ plural(kpis.readyToClaimOrders, 'order') }} · {{ plural(kpis.readyToClaimProducts, 'product') }}</div>
            </div>
            <div class="fee-claims-kpi">
              <div class="k">Claim within 7 days</div>
              <div class="v" :class="{ red: kpis.claimWithin7DaysCents > 0, multi: kpiAmounts('claimWithin7DaysCents').length > 1 }"><span v-for="(a, i) in kpiAmounts('claimWithin7DaysCents')" :key="i">{{ a }}</span></div>
              <div class="s">oldest orders leave Amazon's 90 days</div>
            </div>
            <div class="fee-claims-kpi">
              <div class="k">Filed, waiting</div>
              <div class="v" :class="{ multi: kpiAmounts('filedCents').length > 1 }"><span v-for="(a, i) in kpiAmounts('filedCents')" :key="i">{{ a }}</span></div>
              <div class="s">{{ plural(kpis.filedCount, 'claim') }} open</div>
            </div>
            <div class="fee-claims-kpi">
              <div class="k">Recovered</div>
              <div class="v green" :class="{ multi: kpiAmounts('recoveredCents').length > 1 }"><span v-for="(a, i) in kpiAmounts('recoveredCents')" :key="i">{{ a }}</span></div>
              <div class="s">since you started</div>
            </div>
          </div>

          <div class="fee-claims-sumbar">
            <button
              type="button"
              class="fee-claims-btn green"
              :disabled="!counts.to_claim || downloadingAll"
              @click="downloadAll"
            >
              <i :class="downloadingAll ? 'notched circle loading icon' : 'download icon'"></i>
              Download all claimable orders (XLSX)
            </button>
            <span class="fee-claims-muted">Every product in one file: Summary, Orders to claim, How to file.</span>
            <button type="button" class="fee-claims-howlink" @click="howOpen = !howOpen">How to claim</button>
          </div>
          <div v-if="downloadError" class="fee-claims-error">{{ downloadError }}</div>
          <div v-if="howOpen" class="fee-claims-how">
            <ol>
              <li>Download the order list for the product.</li>
              <li>In Seller Central, open a Seller Support case for FBA fees and attach the file.</li>
              <li>When Amazon refunds it, mark it here as Refunded.</li>
            </ol>
            <div class="fee-claims-muted">Amazon only looks at orders from the last 90 days, so claim the oldest first.</div>
          </div>

          <div class="fee-claims-seg">
            <button
              v-for="t in tabs"
              :key="t.key"
              type="button"
              :class="{ on: filter === t.key }"
              @click="setFilter(t.key)"
            >{{ t.label }} ({{ counts[t.key] || 0 }})</button>
          </div>

          <div class="fee-claims-table">
            <div class="fee-claims-head">
              <div>Product</div>
              <div>What Amazon changed</div>
              <div>{{ isFeeUp ? 'New fee' : 'Orders' }}</div>
              <div>{{ isFeeUp ? 'Extra / unit' : 'Amount' }}</div>
              <div>{{ filter === 'to_claim' ? 'Claim by' : '' }}</div>
              <div>Status</div>
            </div>

            <div v-if="loading && !claims.length" class="fee-claims-empty"><i class="notched circle loading icon"></i> Loading…</div>
            <div v-else-if="loadError" class="fee-claims-empty">{{ loadError }}</div>
            <div v-else-if="!claims.length" class="fee-claims-empty">Nothing here.</div>

            <div v-for="c in claims" :key="c.id" class="fee-claims-row">
              <div class="prod">
                <img v-if="c.imageUrl" :src="c.imageUrl" class="img" alt="" loading="lazy" />
                <div v-else class="img placeholder"><i class="image icon"></i></div>
                <div class="prod-info">
                  <div class="t" :title="c.title">{{ c.title }}</div>
                  <dl class="ids">
                    <dt>SKU</dt><dd class="mono" :title="c.sellerSku">{{ c.sellerSku || '—' }}</dd>
                    <dt>ASIN</dt>
                    <dd class="mono">
                      <a v-if="asinUrl(c)" :href="asinUrl(c)" target="_blank" rel="noopener noreferrer">{{ c.asin }}</a>
                      <template v-else>{{ c.asin || '—' }}</template>
                    </dd>
                    <dt>FNSKU</dt><dd class="mono">{{ c.fnsku || '—' }}</dd>
                  </dl>
                  <span class="mp">{{ c.marketplace }}</span>
                </div>
              </div>

              <div class="what">
                <table class="cmp">
                  <thead>
                    <tr>
                      <th></th>
                      <th>Before</th>
                      <th>After (Amazon now)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="r in comparisonRows(c)" :key="r.measure">
                      <th>{{ measureLabel(r.measure) }}</th>
                      <td class="before">{{ r.before || 'No change' }}</td>
                      <td class="after">
                        {{ r.after || '—' }}
                        <div v-if="r.listing" class="listing" :class="{ differs: r.listingDiffers }">Your listing: {{ r.listing }}</div>
                      </td>
                    </tr>
                    <tr class="fee">
                      <th>FBA fee</th>
                      <td class="before">{{ money(c.previousFeeCents, c.currency) }}</td>
                      <td class="after" :class="isUp(c) ? 'amber' : 'green'">{{ money(c.currentFeeCents, c.currency) }}</td>
                    </tr>
                  </tbody>
                </table>
                <div class="meta">
                  <span>Re-measured {{ shortDate(c.feeChangedOn) }}</span>
                  <span v-if="c.matchesListing" class="tag good"><i class="check icon"></i>Now matches your listing</span>
                  <span v-else-if="c.listingDiffers && c.listingDiffers.length" class="tag warn">Differs from your listing</span>
                  <span v-if="c.reversesChangeOn" class="tag">Reverses the {{ shortDate(c.reversesChangeOn) }} increase</span>
                </div>
              </div>

              <div class="num">
                <template v-if="isUp(c)">{{ money(c.currentFeeCents, c.currency) }}</template>
                <template v-else-if="c.orderListStatus === 'pending'"><span class="fee-claims-muted">Checking…</span></template>
                <template v-else>{{ c.orders != null ? c.orders : '—' }}</template>
              </div>

              <div class="amt" :class="isUp(c) ? 'amber' : 'green'">
                <template v-if="isUp(c)">+{{ money(c.currentFeeCents - c.previousFeeCents, c.currency) }}</template>
                <template v-else-if="c.claimCents != null">{{ money(c.claimCents, c.currency) }}</template>
                <template v-else>—</template>
              </div>

              <div class="when">
                <div v-if="filter === 'to_claim' && c.claimBy" class="date" :class="{ soon: c.claimByDueSoon }">{{ shortDate(c.claimBy) }}</div>
                <button
                  v-if="c.downloadUrl && !isUp(c) && filter !== 'done'"
                  type="button"
                  class="fee-claims-btn green small"
                  :disabled="downloadingId === c.id"
                  @click="downloadOne(c)"
                >
                  <i :class="downloadingId === c.id ? 'notched circle loading icon' : 'download icon'"></i>
                  Download orders
                </button>
                <button v-if="isUp(c) && c.productId" type="button" class="fee-claims-btn small" @click="viewProduct(c)">View product</button>
                <div v-if="rowError.id === c.id" class="fee-claims-error">{{ rowError.message }}</div>
              </div>

              <div>
                <select
                  class="fee-claims-status"
                  :value="c.status"
                  :disabled="savingId === c.id"
                  @change="setStatus(c, $event.target.value)"
                >
                  <option v-for="s in c.statuses" :key="s" :value="s">{{ statusLabel(s) }}</option>
                </select>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
  </DashboardLayout>
</template>

<script>
import DashboardLayout from './components/buybox/DashboardLayout.vue'
import { getCurrencySymbol } from './utils/currency.js'
import { buildProductDetailsQuery } from './utils/listViewUrlSync.js'
import { amazonListingUrl } from './utils/amazonMarketplaceUrls.js'

var API = '/api/v1/fee-claims'
var TABS = [
  { key: 'to_claim', label: 'To claim' },
  { key: 'fee_up', label: 'Fee went up' },
  { key: 'filed', label: 'Filed' },
  { key: 'done', label: 'Done' }
]
var STATUS_LABELS = {
  to_claim: 'To claim',
  filed: 'Filed',
  refunded: 'Refunded',
  declined: 'Declined',
  to_check: 'To check',
  remeasure_requested: 'Asked Amazon to re-measure',
  corrected: 'Amazon corrected it',
  measurement_right: 'Measurement is right'
}
var MEASURE_LABELS = { size_tier: 'Size tier', dimensions: 'Dimensions', weight: 'Weight' }
var MP_SUFFIX = { US: 'com', CA: 'ca', MX: 'com.mx', UK: 'co.uk', GB: 'co.uk', DE: 'de', FR: 'fr', IT: 'it', ES: 'es', JP: 'co.jp', AU: 'com.au' }
var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

// Fee Claims page (tenants with feeClaims): KPIs, one table with four filters, status per row, per-row and
// all-in-one order list downloads. Rows, KPIs and counts come from GET /api/v1/fee-claims.
export default {
  name: 'FeeClaimsPage',
  components: { DashboardLayout },
  data: function () {
    return {
      tabs: TABS,
      // KPI cards hidden for now; the API still returns kpis (FeeClaimsController#kpis). Set true to show them.
      showKpis: false,
      filter: 'to_claim',
      sellerId: '',
      marketplace: '',
      claims: [],
      kpis: {},
      counts: {},
      sellerOptions: [],
      marketplaceOptions: [],
      loading: false,
      loadError: '',
      notAvailable: false,
      savingId: null,
      downloadingAll: false,
      downloadError: '',
      downloadingId: null,
      rowError: { id: null, message: '' },
      howOpen: false
    }
  },
  computed: {
    isFeeUp: function () { return this.filter === 'fee_up' }
  },
  mounted: function () {
    var q = this.$route.query || {}
    if (TABS.some(function (t) { return t.key === q.filter })) this.filter = q.filter
    this.load()
  },
  methods: {
    params: function () {
      var p = { filter: this.filter }
      if (this.sellerId) p.seller_ids = this.sellerId
      if (this.marketplace) p.marketplaces = this.marketplace
      return p
    },
    load: function () {
      var self = this
      var gen = (this._gen = (this._gen || 0) + 1)
      this.loading = true
      this.loadError = ''
      return this.$http.get(API, { params: this.params() }).then(function (res) {
        if (gen !== self._gen) return
        var body = res.body || {}
        self.claims = body.claims || []
        self.kpis = body.kpis || {}
        self.counts = body.counts || {}
        var f = body.filters || {}
        self.sellerOptions = f.sellers || []
        self.marketplaceOptions = f.marketplaces || []
        self.$root.feeClaimsToClaimCount = body.toClaimCount != null ? body.toClaimCount : self.$root.feeClaimsToClaimCount
        self.loading = false
      }, function (res) {
        if (gen !== self._gen) return
        self.loading = false
        if (res && res.status === 404) {
          self.notAvailable = true
        } else {
          self.loadError = "Couldn't load fee claims. Please try again."
        }
      })
    },
    setFilter: function (key) {
      if (this.filter === key) return
      this.filter = key
      this.claims = []
      this.$router.replace({ path: this.$route.path, query: Object.assign({}, this.$route.query, { filter: key }) })
      this.load()
    },
    setStatus: function (claim, status) {
      var self = this
      if (status === claim.status) return
      this.savingId = claim.id
      this.$http.patch(API + '/' + claim.id, { status: status }).then(function () {
        self.savingId = null
        self.load()
        if (self.$root.loadFeeClaimsCount) self.$root.loadFeeClaimsCount(true)
      }, function (res) {
        self.savingId = null
        var msg = res && res.body && res.body.message
        window.alert(msg || "Couldn't update the status. Please try again.")
        self.load()
      })
    },
    downloadAll: function () {
      var self = this
      var p = this.params()
      delete p.filter
      var qs = Object.keys(p).map(function (k) { return encodeURIComponent(k) + '=' + encodeURIComponent(p[k]) }).join('&')
      this.downloadingAll = true
      this.downloadError = ''
      this.saveFile(API + '/download' + (qs ? '?' + qs : ''), 'SentryKit - FBA Fee Claims.xlsx', function (status) {
        self.downloadingAll = false
        if (status !== 200) self.downloadError = status === 404 ? 'Nothing to claim right now.' : "Couldn't build the file. Please try again."
      })
    },
    // One claim's order list through the API (same origin + session as the page), not the email's signed link.
    downloadOne: function (claim) {
      var self = this
      this.downloadingId = claim.id
      this.rowError = { id: null, message: '' }
      this.saveFile(API + '/' + claim.id + '/download', 'SentryKit - FBA Fee Claim - ' + claim.sellerSku + '.xlsx', function (status) {
        self.downloadingId = null
        if (status !== 200) {
          self.rowError = { id: claim.id, message: status === 404 ? "The order list isn't ready yet." : "Couldn't download the file. Please try again." }
        }
      })
    },
    // Same XHR + blob pattern as the costs CSV template: the session cookie authenticates it. done(status); a 200 that
    // isn't a spreadsheet (an HTML or JSON page) is reported as a failure (0) instead of being saved as a broken .xlsx.
    saveFile: function (path, fallbackName, done) {
      var xhr = new XMLHttpRequest()
      xhr.open('GET', path, true)
      xhr.responseType = 'blob'
      xhr.onload = function () {
        var isXlsx = (xhr.getResponseHeader('Content-Type') || '').indexOf('spreadsheetml') !== -1
        if (xhr.status === 200 && !isXlsx) return done(0)
        if (xhr.status === 200) {
          var match = (xhr.getResponseHeader('Content-Disposition') || '').match(/filename="?([^";]+)"?/)
          var url = window.URL.createObjectURL(xhr.response)
          var link = document.createElement('a')
          link.href = url
          link.download = match ? match[1] : fallbackName
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          setTimeout(function () { window.URL.revokeObjectURL(url) }, 1000)
        }
        done(xhr.status)
      }
      xhr.onerror = function () { done(0) }
      xhr.send()
    },
    // Before / after / listing per measure (API comparison); older rows without it fall back to the evidence text.
    comparisonRows: function (claim) {
      if (claim.comparison && claim.comparison.length) return claim.comparison
      return (claim.evidence || []).map(function (e) {
        var sides = String(e.text).split(' → ')
        return { measure: e.measure, before: sides.length > 1 ? sides[0] : null, after: sides.length > 1 ? sides.slice(1).join(' → ') : e.text }
      })
    },
    asinUrl: function (claim) { return claim.asin ? amazonListingUrl(claim.asin, claim.marketplace) : null },
    viewProduct: function (claim) {
      this.$router.push({ path: '/product_details', query: buildProductDetailsQuery(String(claim.productId), this.$route.fullPath) })
    },
    isUp: function (claim) { return claim.reason === 'remeasured_up' },
    statusLabel: function (s) { return STATUS_LABELS[s] || s },
    measureLabel: function (m) { return MEASURE_LABELS[m] || m },
    mpSuffix: function (m) { return MP_SUFFIX[m] || String(m).toLowerCase() },
    plural: function (n, word) {
      var v = n || 0
      return v + ' ' + word + (v === 1 ? '' : 's')
    },
    // One KPI in each marketplace's own currency ("$41.86", "CA$12.00"), never added up across currencies.
    // Currencies with nothing in this KPI are left out; all zero shows the first one's 0.
    kpiAmounts: function (key) {
      var self = this
      var list = this.kpis.byCurrency || []
      if (list.length <= 1) {
        return [this.money(this.kpis[key], this.kpis.currency || (list[0] && list[0].currency) || 'USD')]
      }
      var shown = list.filter(function (c) { return c[key] > 0 })
      if (!shown.length) shown = [list[0]]
      return shown.map(function (c) { return self.money(c[key], c.currency) })
    },
    // "$1.47", "CA$1.47" (currency symbols from utils/currency.js).
    money: function (cents, currency) {
      var amount = Math.abs((cents || 0) / 100).toFixed(2)
      var sign = cents < 0 ? '-' : ''
      return currency === null ? sign + amount : sign + getCurrencySymbol(currency || 'USD') + amount
    },
    shortDate: function (iso) {
      if (!iso) return ''
      var parts = String(iso).split('-')
      if (parts.length < 3) return iso
      return MONTHS[parseInt(parts[1], 10) - 1] + ' ' + parseInt(parts[2], 10)
    }
  }
}
</script>

<style scoped>
.fee-claims { min-height: 100vh; background: #f8fafc; color: #1e293b; }
.fee-claims-content { padding: 32px; max-width: 1380px; }
.fee-claims-title { font-size: 22px; font-weight: 700; margin: 0 0 4px; }
.fee-claims-subtitle { font-size: 13px; color: #64748b; margin: 0; }
.fee-claims-muted { color: #64748b; font-size: 12.5px; }
.fee-claims-filters { display: flex; gap: 8px; }
.fee-claims-filter { border: 1px solid #e2e8f0; border-radius: 7px; padding: 6px 10px; font-size: 12.5px; background: #fff; }

.fee-claims-kpis { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-top: 18px; }
.fee-claims-kpi { background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; padding: 12px 14px; }
.fee-claims-kpi .k { font-size: 11px; letter-spacing: .05em; color: #64748b; font-weight: 700; text-transform: uppercase; }
.fee-claims-kpi .v { font-size: 24px; font-weight: 900; margin-top: 2px; }
.fee-claims-kpi .v.multi { font-size: 18px; line-height: 1.3; }
.fee-claims-kpi .v.multi span { display: block; }
.fee-claims-kpi .s { font-size: 12px; color: #64748b; }
.green { color: #15803d; }
.red { color: #dc2626; }
.amber { color: #d97706; }

.fee-claims-sumbar { display: flex; align-items: center; gap: 12px; margin-top: 14px; flex-wrap: wrap; }
.fee-claims-btn { border: 1px solid #e2e8f0; background: #fff; border-radius: 6px; padding: 7px 12px; font-size: 12.5px; color: #1e293b; cursor: pointer; white-space: nowrap; display: inline-block; text-decoration: none; }
.fee-claims-btn.green { background: #16a34a; border-color: #16a34a; color: #fff; font-weight: 700; }
.fee-claims-btn.small { padding: 5px 10px; margin-top: 6px; }
.fee-claims-btn:disabled { opacity: .5; cursor: default; }
.fee-claims-btn .icon { margin: 0 4px 0 0; }
.fee-claims-howlink { font-size: 13px; color: #1b4b82; font-weight: 700; background: none; border: 0; text-decoration: underline; cursor: pointer; }
.fee-claims-how { margin-top: 10px; background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; padding: 14px 16px; font-size: 13px; line-height: 1.6; max-width: 640px; }
.fee-claims-how ol { padding-left: 18px; margin: 0 0 6px; }
.fee-claims-error { margin-top: 8px; color: #dc2626; font-size: 12.5px; }

.fee-claims-seg { display: flex; gap: 6px; margin-top: 16px; flex-wrap: wrap; }
.fee-claims-seg button { border: 1px solid #e2e8f0; background: #fff; border-radius: 18px; padding: 6px 13px; font-size: 12.5px; color: #1e293b; cursor: pointer; }
.fee-claims-seg button.on { background: #1b4b82; border-color: #1b4b82; color: #fff; font-weight: 700; }

.fee-claims-table { background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; margin-top: 12px; overflow: hidden; }
.fee-claims-head, .fee-claims-row { display: grid; grid-template-columns: minmax(210px, 1fr) minmax(360px, 1.6fr) 64px 90px 136px 160px; gap: 16px; align-items: center; }
.fee-claims-head { padding: 10px 18px; font-size: 11px; letter-spacing: .05em; color: #64748b; font-weight: 700; text-transform: uppercase; background: #f8fafc; }
.fee-claims-row { padding: 16px 18px; border-top: 1px solid #e2e8f0; }
.fee-claims-row:hover { background: #fcfdfe; }

.fee-claims-row .prod { display: flex; gap: 12px; align-items: flex-start; min-width: 0; }
.fee-claims-row .img { width: 48px; height: 48px; border-radius: 8px; object-fit: contain; background: #fff; border: 1px solid #e2e8f0; flex: none; }
.fee-claims-row .img.placeholder { display: flex; align-items: center; justify-content: center; color: #94a3b8; background: #eef2f7; }
.fee-claims-row .prod-info { min-width: 0; flex: 1; }
.fee-claims-row .t { font-size: 13px; font-weight: 600; line-height: 1.35; display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; }
.fee-claims-row .ids { display: grid; grid-template-columns: auto 1fr; column-gap: 8px; row-gap: 1px; margin: 6px 0 0; font-size: 12px; }
.fee-claims-row .ids dt { color: #94a3b8; font-size: 10.5px; font-weight: 700; letter-spacing: .04em; line-height: 18px; }
.fee-claims-row .ids dd { margin: 0; line-height: 18px; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; user-select: all; }
.fee-claims-row .ids a { color: #1b4b82; text-decoration: underline; }
.fee-claims-row .mono { font-family: ui-monospace, Menlo, monospace; color: #334155; }
.fee-claims-row .mp { display: inline-block; margin-top: 6px; padding: 1px 7px; border-radius: 4px; background: #eef2f7; color: #475569; font-size: 11px; font-weight: 700; }

.fee-claims-row .what { font-size: 12.5px; min-width: 0; }
.fee-claims-row .cmp { width: 100%; border-collapse: collapse; table-layout: auto; }
.fee-claims-row .cmp th, .fee-claims-row .cmp td { padding: 4px 8px; text-align: left; vertical-align: top; line-height: 1.35; }
.fee-claims-row .cmp thead th { font-size: 10.5px; font-weight: 700; letter-spacing: .04em; text-transform: uppercase; color: #94a3b8; padding-top: 0; }
.fee-claims-row .cmp tbody tr { border-top: 1px solid #f1f5f9; }
.fee-claims-row .cmp tbody th { font-weight: 600; color: #475569; white-space: nowrap; padding-left: 0; }
.fee-claims-row .cmp thead th:first-child { padding-left: 0; }
.fee-claims-row .cmp .before { color: #64748b; }
.fee-claims-row .cmp .after { color: #0f172a; font-weight: 700; }
.fee-claims-row .cmp td { white-space: nowrap; }
.fee-claims-row .cmp .listing { font-size: 11.5px; font-weight: 400; color: #64748b; margin-top: 1px; }
.fee-claims-row .cmp .listing.differs { color: #b45309; font-weight: 600; }
.fee-claims-row .cmp tr.fee td.after.green { color: #15803d; }
.fee-claims-row .cmp tr.fee td.after.amber { color: #d97706; }
.fee-claims-row .meta { display: flex; flex-wrap: wrap; align-items: center; gap: 6px 10px; margin-top: 8px; font-size: 12px; color: #64748b; }
.fee-claims-row .tag { display: inline-flex; align-items: center; padding: 2px 8px; border-radius: 10px; background: #f1f5f9; color: #475569; font-weight: 600; }
.fee-claims-row .tag .icon { margin: 0 3px 0 0; font-size: 10px; height: auto; }
.fee-claims-row .tag.good { background: #dcfce7; color: #15803d; }
.fee-claims-row .tag.warn { background: #fef3c7; color: #b45309; }

.fee-claims-row .num { font-size: 14px; font-weight: 600; }
.fee-claims-row .amt { font-weight: 900; font-size: 16px; }
.fee-claims-row .when { font-size: 13px; display: flex; flex-direction: column; align-items: flex-start; gap: 6px; }
.fee-claims-row .when .date { font-weight: 600; }
.fee-claims-row .when .fee-claims-btn.small { margin-top: 0; }
.fee-claims-row .when .fee-claims-error { margin-top: 0; }
.fee-claims-row .soon { color: #dc2626; font-weight: 700; }
.fee-claims-btn.green, .fee-claims-btn.green:hover, .fee-claims-btn.green:focus { color: #fff !important; }
.fee-claims-btn.green:hover:not(:disabled) { background: #15803d; border-color: #15803d; }
.fee-claims-status { border: 1px solid #e2e8f0; border-radius: 6px; padding: 7px 8px; font-size: 12.5px; width: 100%; background: #fff; cursor: pointer; }
.fee-claims-empty { padding: 28px; text-align: center; color: #64748b; }

@media (max-width: 1100px) {
  .fee-claims-kpis { grid-template-columns: repeat(2, 1fr); }
  .fee-claims-head { display: none; }
  .fee-claims-row { grid-template-columns: 1fr 1fr; }
  .fee-claims-row .prod, .fee-claims-row .what { grid-column: 1 / -1; }
}
</style>



// WEBPACK FOOTER //
// FeeClaimsPage.vue?bcffc448